import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { motion } from 'motion/react';
import { useMemo, useState, type ReactNode } from 'react';
import { Icon } from '../components/Icon.tsx';
import { EASE, Reveal } from '../components/motion.tsx';
import { Button, Callout, Chip, Dialog, Empty, Field, PageHead, Panel, Skeleton, Switch, Tabs, useConfirm, useToast } from '../components/ui.tsx';
import { api, ApiError, get, patch, post, put } from '../lib/api.ts';
import { relative } from '../lib/format.ts';
import { useMe } from '../lib/session.ts';

interface Overview {
  companies: { id: string; code: string; name: string; isPrimary: boolean; taxRegime: string }[];
  offices: { id: string; name: string; companyId: string; code: string | null }[];
  brands: { id: string; name: string; description: string | null; website: string | null; deletedAt: string | null }[];
  companyBrands: { id: string; companyId: string; brandId: string; officeId: string | null }[];
  productTypes: { id: string; name: string; description: string | null; deletedAt: string | null }[];
  products: { id: string; companyBrandId: string; productTypeId: string; name: string; description: string | null; properties: Record<string, unknown>; deletedAt: string | null }[];
  warehouses: Warehouse[];
  taxRates: { id: string; type: 'ppn' | 'pph23'; name: string; percentage: string; active: boolean; createdAt: string }[];
  links: { id: string; companyId: string; officeId: string | null; name: string; identifier: string | null; url: string; enabled: boolean }[];
  documents: { id: string; companyId: string; name: string; identifier: string | null; fileId: string | null; deletedAt: string | null }[];
  settings: { id: string; companyId: string; officeId: string | null; key: string; value: string | null; json: unknown }[];
  nationalities: number;
}
interface Warehouse {
  id: string;
  officeId: string;
  parentId: string | null;
  name: string;
  displayName: string | null;
  stockChecked: boolean;
  contactName: string | null;
  phone: string | null;
  mobile: string | null;
  email: string | null;
  address: string | null;
  notes: string | null;
  deletedAt: string | null;
}
interface Integrations {
  outbox: { lastId: number; retained: number };
  consumers: { name: 'erp_mysql' | 'rabbitmq_company'; configured: boolean; lastId: number; behind: number; lastRunAt: string | null; failures: number; lastError: string | null }[];
  gateway: { trustedNetworks: number };
}

type Tab = 'brands' | 'warehouses' | 'taxes' | 'links' | 'companies';
const CONSUMER: Record<Integrations['consumers'][number]['name'], { label: string; detail: string }> = {
  erp_mysql: { label: 'ERP database', detail: 'Tables other GX apps read through erp-manager' },
  rabbitmq_company: { label: 'Company events', detail: 'enterprise.company.save / delete on RabbitMQ' },
};

const errText = (e: unknown) => (e instanceof ApiError ? e.message : 'Something went wrong.');

export function Company() {
  const me = useMe();
  const canEdit = !!me.data?.access.masterEdit;
  const q = useQuery({ queryKey: ['master'], queryFn: () => get<Overview>('/api/master/overview') });
  const integ = useQuery({ queryKey: ['master-integrations'], queryFn: () => get<Integrations>('/api/master/integrations'), refetchInterval: 15_000 });
  const [tab, setTab] = useState<Tab>('brands');
  const d = q.data;
  const live = (xs: { deletedAt: string | null }[] | undefined) => xs?.filter((x) => !x.deletedAt).length ?? 0;

  return (
    <div>
      <PageHead
        eyebrow={`Company · ${d?.companies.length ?? '…'} companies · ${d?.offices.length ?? '…'} offices`}
        title={
          <>
            What the group sells, <em>stores and charges.</em>
          </>
        }
        lede="Master data that used to live in the Enterprise Platform. Other GX apps still read it from the old ERP database, so every change here is mirrored there, with the same ids, until they move to the API."
        aside={<Bridge data={integ.data} />}
      />
      <Tabs
        idPrefix="company"
        value={tab}
        onChange={setTab}
        tabs={[
          { id: 'brands', label: 'Brands & products', count: live(d?.brands) },
          { id: 'warehouses', label: 'Warehouses', count: live(d?.warehouses) },
          { id: 'taxes', label: 'Sales tax', count: d?.taxRates.filter((t) => t.active).length },
          { id: 'links', label: 'Links & settings', count: (d?.links.length ?? 0) + (d?.settings.length ?? 0) },
          { id: 'companies', label: 'Companies & offices', count: d?.offices.length },
        ]}
      />
      <div style={{ marginTop: 28 }}>
        {q.isLoading || !d ? (
          <Skeleton h={320} />
        ) : tab === 'brands' ? (
          <Brands d={d} canEdit={canEdit} />
        ) : tab === 'warehouses' ? (
          <Warehouses d={d} canEdit={canEdit} />
        ) : tab === 'taxes' ? (
          <Taxes d={d} canEdit={canEdit} />
        ) : tab === 'links' ? (
          <Links d={d} canEdit={canEdit} />
        ) : (
          <Companies d={d} canEdit={canEdit} />
        )}
      </div>
    </div>
  );
}

/** Health of the legacy bridge: is the old world still hearing about changes? */
function Bridge({ data }: { data?: Integrations }) {
  if (!data) return null;
  return (
    <div className="stack" style={{ ['--gap' as string]: '8px', minWidth: 250 }}>
      <div className="eyebrow">Legacy bridge</div>
      {data.consumers.map((c) => {
        const tone = !c.configured ? 'plain' : c.failures ? 'bad' : c.behind > 50 ? 'warn' : 'ok';
        return (
          <div key={c.name} className="row row--between" style={{ gap: 12 }} title={c.lastError ?? CONSUMER[c.name].detail}>
            <span style={{ fontSize: 'var(--step--1)' }}>{CONSUMER[c.name].label}</span>
            <Chip tone={tone} live={c.configured && !c.failures}>
              {!c.configured ? 'Off' : c.failures ? `Retrying · ${c.failures}` : c.behind ? `${c.behind} pending` : c.lastRunAt ? `In sync · ${relative(c.lastRunAt)}` : 'In sync'}
            </Chip>
          </div>
        );
      })}
    </div>
  );
}

function useSave<T>(fn: (v: T) => Promise<unknown>, done?: () => void) {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['master'] });
      void qc.invalidateQueries({ queryKey: ['master-integrations'] });
      done?.();
    },
    onError: (e) => toast({ title: errText(e), tone: 'error' }),
  });
}

// ---------------------------------------------------------------- Brands & products

function Brands({ d, canEdit }: { d: Overview; canEdit: boolean }) {
  const [brandId, setBrandId] = useState<string | null>(null);
  const [dialog, setDialog] = useState<null | 'brand' | 'product' | 'carry' | 'type'>(null);
  const brands = d.brands.filter((b) => !b.deletedAt);
  const active = brandId ?? brands[0]?.id ?? null;
  const company = (id: string) => d.companies.find((c) => c.id === id);
  const carried = d.companyBrands.filter((cb) => cb.brandId === active);
  const products = d.products.filter((p) => !p.deletedAt && carried.some((cb) => cb.id === p.companyBrandId));
  const type = (id: string) => d.productTypes.find((t) => t.id === id)?.name ?? '—';
  const archive = useSave((p: { id: string }) => patch(`/api/master/products/${p.id}`, { archived: true }));

  if (!brands.length)
    return (
      <Empty title="No brands yet." action={canEdit ? <Button variant="primary" icon="plus" onClick={() => setDialog('brand')}>Add brand</Button> : undefined}>
        Brands are what customers see (internet, media, retail). Products hang off a brand as carried by one company.
        <BrandDialog open={dialog === 'brand'} onClose={() => setDialog(null)} />
      </Empty>
    );

  return (
    <Reveal className="grid-12" as="section">
      <Reveal.Item className="col-4 stack" style={{ alignContent: 'start', ['--gap' as string]: '10px' }}>
        <div className="row row--between">
          <div className="eyebrow">Brands · {brands.length}</div>
          {canEdit ? <Button size="sm" variant="ghost" icon="plus" onClick={() => setDialog('brand')}>Brand</Button> : null}
        </div>
        {brands.map((b, i) => {
          const n = d.companyBrands.filter((cb) => cb.brandId === b.id).length;
          return (
            <motion.button key={b.id} type="button" className="org-node" aria-pressed={b.id === active} onClick={() => setBrandId(b.id)} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.45, ease: EASE, delay: i * 0.04 }}>
              <span className="org-node__name">{b.name}</span>
              <span className="display num" style={{ fontSize: '1.3rem', color: b.id === active ? 'var(--accent-text)' : 'var(--ink-2)' }}>{n}</span>
              <span className="eyebrow" style={{ gridColumn: '1 / -1' }}>{b.website ? new URL(b.website).host : (b.description ?? 'brand')}</span>
            </motion.button>
          );
        })}
      </Reveal.Item>

      <Reveal.Item className="col-8 stack">
        <Panel
          eyebrow="Carried by"
          title={brands.find((b) => b.id === active)?.name}
          action={canEdit ? <Button size="sm" icon="plus" onClick={() => setDialog('carry')}>Company</Button> : undefined}
        >
          {carried.length ? (
            <div className="row" style={{ flexWrap: 'wrap', gap: 8 }}>
              {carried.map((cb) => (
                <Chip key={cb.id} tone={company(cb.companyId)?.isPrimary ? 'info' : 'plain'}>
                  {company(cb.companyId)?.code ?? '—'}
                  {cb.officeId ? ` · ${d.offices.find((o) => o.id === cb.officeId)?.name}` : ''}
                </Chip>
              ))}
            </div>
          ) : (
            <p className="muted">No company carries this brand yet.</p>
          )}
        </Panel>
        <Panel
          eyebrow={`Products · ${products.length}`}
          title="What is sold under it"
          flush
          action={
            canEdit ? (
              <div className="row" style={{ gap: 8 }}>
                <Button size="sm" variant="ghost" onClick={() => setDialog('type')}>Product type</Button>
                <Button size="sm" variant="primary" icon="plus" disabled={!carried.length} onClick={() => setDialog('product')}>Product</Button>
              </div>
            ) : undefined
          }
        >
          {products.length ? (
            <table className="table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Type</th>
                  <th>Company</th>
                  <th>Properties</th>
                  {canEdit ? <th className="right" aria-label="Actions" /> : null}
                </tr>
              </thead>
              <tbody>
                {products.map((p) => {
                  const cb = d.companyBrands.find((x) => x.id === p.companyBrandId);
                  return (
                    <tr key={p.id}>
                      <td>
                        <div>{p.name}</div>
                        {p.description ? <div className="faint" style={{ fontSize: 'var(--step--1)' }}>{p.description}</div> : null}
                      </td>
                      <td className="muted">{type(p.productTypeId)}</td>
                      <td className="mono">{cb ? company(cb.companyId)?.code : '—'}</td>
                      <td className="mono faint" style={{ fontSize: 'var(--step--1)' }}>
                        {Object.entries(p.properties ?? {}).slice(0, 3).map(([k, v]) => `${k}: ${String(v)}`).join(' · ') || '—'}
                      </td>
                      {canEdit ? (
                        <td className="right">
                          <Button size="sm" variant="ghost" onClick={() => archive.mutate({ id: p.id })}>Archive</Button>
                        </td>
                      ) : null}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <div style={{ padding: 22 }}>
              <p className="muted">{carried.length ? 'No products yet.' : 'Add a company that carries this brand first.'}</p>
            </div>
          )}
        </Panel>
      </Reveal.Item>
      <BrandDialog open={dialog === 'brand'} onClose={() => setDialog(null)} />
      <CarryDialog open={dialog === 'carry'} onClose={() => setDialog(null)} d={d} brandId={active} />
      <ProductTypeDialog open={dialog === 'type'} onClose={() => setDialog(null)} />
      <ProductDialog open={dialog === 'product'} onClose={() => setDialog(null)} d={d} carried={carried} />
    </Reveal>
  );
}

function Form({ open, onClose, eyebrow, title, onSubmit, pending, error, children, submit = 'Save' }: { open: boolean; onClose: () => void; eyebrow: string; title: ReactNode; onSubmit: () => void; pending: boolean; error: unknown; children: ReactNode; submit?: string }) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      eyebrow={eyebrow}
      title={title}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={pending} onClick={onSubmit}>{submit}</Button>
        </>
      }
    >
      <div className="grid-12" style={{ gap: 14 }}>
        {children}
        {error ? (
          <div className="col-12">
            <Callout tone="bad">{errText(error)}</Callout>
          </div>
        ) : null}
      </div>
    </Dialog>
  );
}

function BrandDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [f, setF] = useState({ name: '', description: '', website: '' });
  const m = useSave(() => post('/api/master/brands', { name: f.name, description: f.description || null, website: f.website || null }), () => { setF({ name: '', description: '', website: '' }); onClose(); });
  return (
    <Form open={open} onClose={onClose} eyebrow="New brand" title="Add a brand" onSubmit={() => m.mutate(undefined)} pending={m.isPending} error={m.error}>
      <div className="col-12"><Field label="Name">{(p) => <input {...p} className="input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />}</Field></div>
      <div className="col-12"><Field label="Description">{(p) => <input {...p} className="input" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />}</Field></div>
      <div className="col-12"><Field label="Website">{(p) => <input {...p} className="input input--mono" value={f.website} placeholder="https://" onChange={(e) => setF({ ...f, website: e.target.value })} />}</Field></div>
    </Form>
  );
}

function CarryDialog({ open, onClose, d, brandId }: { open: boolean; onClose: () => void; d: Overview; brandId: string | null }) {
  const [f, setF] = useState({ companyId: '', officeId: '' });
  const companyId = f.companyId || d.companies[0]?.id || '';
  const m = useSave(() => post('/api/master/company-brands', { companyId, brandId, officeId: f.officeId || null }), onClose);
  return (
    <Form open={open} onClose={onClose} eyebrow="Brand" title="A company carries this brand" onSubmit={() => m.mutate(undefined)} pending={m.isPending} error={m.error} submit="Add">
      <div className="col-6">
        <Field label="Company">{(p) => <select {...p} className="select" value={companyId} onChange={(e) => setF({ companyId: e.target.value, officeId: '' })}>{d.companies.map((c) => <option key={c.id} value={c.id}>{c.code} — {c.name}</option>)}</select>}</Field>
      </div>
      <div className="col-6">
        <Field label="Office" hint="Optional: only this office sells it">{(p) => <select {...p} className="select" value={f.officeId} onChange={(e) => setF({ ...f, officeId: e.target.value })}><option value="">All offices</option>{d.offices.filter((o) => o.companyId === companyId).map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}</select>}</Field>
      </div>
    </Form>
  );
}

function ProductTypeDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [name, setName] = useState('');
  const m = useSave(() => post('/api/master/product-types', { name }), () => { setName(''); onClose(); });
  return (
    <Form open={open} onClose={onClose} eyebrow="Catalogue" title="Add a product type" onSubmit={() => m.mutate(undefined)} pending={m.isPending} error={m.error}>
      <div className="col-12"><Field label="Name" hint="e.g. Internet plan, Hardware, Installation">{(p) => <input {...p} className="input" value={name} onChange={(e) => setName(e.target.value)} />}</Field></div>
    </Form>
  );
}

function ProductDialog({ open, onClose, d, carried }: { open: boolean; onClose: () => void; d: Overview; carried: Overview['companyBrands'] }) {
  const [f, setF] = useState({ companyBrandId: '', productTypeId: '', name: '', description: '', properties: '' });
  const [jsonError, setJsonError] = useState<string | null>(null);
  const types = d.productTypes.filter((t) => !t.deletedAt);
  const m = useSave(
    () => {
      let properties: Record<string, unknown> = {};
      if (f.properties.trim()) properties = JSON.parse(f.properties) as Record<string, unknown>;
      return post('/api/master/products', { companyBrandId: f.companyBrandId || carried[0]?.id, productTypeId: f.productTypeId || types[0]?.id, name: f.name, description: f.description || null, properties });
    },
    () => { setF({ companyBrandId: '', productTypeId: '', name: '', description: '', properties: '' }); onClose(); },
  );
  const submit = () => {
    try {
      if (f.properties.trim()) JSON.parse(f.properties);
      setJsonError(null);
      m.mutate(undefined);
    } catch {
      setJsonError('Properties must be a JSON object, e.g. {"speedMbps": 50}');
    }
  };
  return (
    <Form open={open} onClose={onClose} eyebrow="New product" title="Add a product" onSubmit={submit} pending={m.isPending} error={m.error}>
      <div className="col-6">
        <Field label="Sold by">{(p) => <select {...p} className="select" value={f.companyBrandId} onChange={(e) => setF({ ...f, companyBrandId: e.target.value })}>{carried.map((cb) => <option key={cb.id} value={cb.id}>{d.companies.find((c) => c.id === cb.companyId)?.code}{cb.officeId ? ` · ${d.offices.find((o) => o.id === cb.officeId)?.name}` : ''}</option>)}</select>}</Field>
      </div>
      <div className="col-6">
        <Field label="Type">{(p) => <select {...p} className="select" value={f.productTypeId} onChange={(e) => setF({ ...f, productTypeId: e.target.value })}>{types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}</select>}</Field>
      </div>
      <div className="col-12"><Field label="Name">{(p) => <input {...p} className="input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />}</Field></div>
      <div className="col-12"><Field label="Description">{(p) => <input {...p} className="input" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />}</Field></div>
      <div className="col-12">
        <Field label="Properties" hint="JSON — read by CRM and sales apps" error={jsonError}>{(p) => <textarea {...p} className="input input--mono" rows={3} value={f.properties} placeholder='{"speedMbps": 50}' onChange={(e) => setF({ ...f, properties: e.target.value })} />}</Field>
      </div>
    </Form>
  );
}

// ---------------------------------------------------------------- Warehouses

function Warehouses({ d, canEdit }: { d: Overview; canEdit: boolean }) {
  const [edit, setEdit] = useState<Partial<Warehouse> | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const list = d.warehouses.filter((w) => showArchived || !w.deletedAt);
  const byOffice = useMemo(() => {
    const out = new Map<string, { w: Warehouse; depth: number }[]>();
    for (const o of d.offices) {
      const mine = list.filter((w) => w.officeId === o.id);
      const walk = (parent: string | null, depth: number): { w: Warehouse; depth: number }[] => mine.filter((w) => w.parentId === parent).flatMap((w) => [{ w, depth }, ...walk(w.id, depth + 1)]);
      // Orphans (parent archived or elsewhere) still show, at the top level.
      const rows = walk(null, 0);
      for (const w of mine) if (!rows.some((r) => r.w.id === w.id)) rows.push({ w, depth: 0 });
      if (rows.length) out.set(o.id, rows);
    }
    return out;
  }, [d.offices, list]);

  return (
    <Reveal className="stack">
      <Reveal.Item className="row row--between">
        <Switch checked={showArchived} onChange={setShowArchived} label="Show archived" showLabel />
        {canEdit ? <Button variant="primary" icon="plus" onClick={() => setEdit({ officeId: d.offices[0]?.id, stockChecked: true })}>Add warehouse</Button> : null}
      </Reveal.Item>
      {!byOffice.size ? (
        <Reveal.Item><Empty title="No warehouses yet.">Inventory and work-order apps pick stock locations from this list.</Empty></Reveal.Item>
      ) : (
        [...byOffice].map(([officeId, rows]) => (
          <Reveal.Item key={officeId}>
            <Panel eyebrow={`${rows.length} locations`} title={d.offices.find((o) => o.id === officeId)?.name} flush>
              <table className="table">
                <thead>
                  <tr>
                    <th>Warehouse</th>
                    <th>Contact</th>
                    <th>Stock-take</th>
                    {canEdit ? <th aria-label="Actions" /> : null}
                  </tr>
                </thead>
                <tbody>
                  {rows.map(({ w, depth }) => (
                    <tr key={w.id} style={{ opacity: w.deletedAt ? 0.5 : 1 }}>
                      <td>
                        <div className="row" style={{ gap: 10, paddingLeft: depth * 22 }}>
                          {depth ? <span className="faint" aria-hidden="true">└</span> : <Icon name="box" width={16} height={16} />}
                          <div>
                            <div>{w.name}</div>
                            {w.displayName && w.displayName !== w.name ? <div className="faint" style={{ fontSize: 'var(--step--1)' }}>Shown as “{w.displayName}”</div> : null}
                          </div>
                        </div>
                      </td>
                      <td className="muted" style={{ fontSize: 'var(--step--1)' }}>{[w.contactName, w.mobile ?? w.phone].filter(Boolean).join(' · ') || '—'}</td>
                      <td>{w.deletedAt ? <Chip>Archived</Chip> : w.stockChecked ? <Chip tone="ok">Counted</Chip> : <Chip>Not counted</Chip>}</td>
                      {canEdit ? <td className="right"><Button size="sm" variant="ghost" onClick={() => setEdit(w)}>Edit</Button></td> : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </Panel>
          </Reveal.Item>
        ))
      )}
      {edit ? <WarehouseDialog d={d} value={edit} onClose={() => setEdit(null)} /> : null}
    </Reveal>
  );
}

function WarehouseDialog({ d, value, onClose }: { d: Overview; value: Partial<Warehouse>; onClose: () => void }) {
  const [f, setF] = useState<Partial<Warehouse>>(value);
  const set = (k: keyof Warehouse) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value || null });
  const parents = d.warehouses.filter((w) => !w.deletedAt && w.officeId === f.officeId && w.id !== f.id);
  const body = { officeId: f.officeId, parentId: f.parentId || null, name: f.name, displayName: f.displayName || null, stockChecked: !!f.stockChecked, contactName: f.contactName || null, phone: f.phone || null, mobile: f.mobile || null, email: f.email || null, address: f.address || null, notes: f.notes || null };
  const m = useSave(() => (f.id ? patch(`/api/master/warehouses/${f.id}`, body) : post('/api/master/warehouses', body)), onClose);
  const arch = useSave(() => patch(`/api/master/warehouses/${f.id}`, { archived: !value.deletedAt }), onClose);
  return (
    <Dialog
      open
      onClose={onClose}
      eyebrow={f.id ? 'Warehouse' : 'New warehouse'}
      title={f.id ? (value.name ?? 'Warehouse') : 'Add a warehouse'}
      width={620}
      footer={
        <>
          {f.id ? <Button variant="ghost" loading={arch.isPending} onClick={() => arch.mutate(undefined)}>{value.deletedAt ? 'Restore' : 'Archive'}</Button> : null}
          <span style={{ flex: 1 }} />
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={m.isPending} onClick={() => m.mutate(undefined)}>Save</Button>
        </>
      }
    >
      <div className="grid-12" style={{ gap: 14 }}>
        <div className="col-6"><Field label="Office">{(p) => <select {...p} className="select" value={f.officeId ?? ''} onChange={(e) => setF({ ...f, officeId: e.target.value, parentId: null })}>{d.offices.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}</select>}</Field></div>
        <div className="col-6"><Field label="Inside">{(p) => <select {...p} className="select" value={f.parentId ?? ''} onChange={set('parentId')}><option value="">— top level —</option>{parents.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}</select>}</Field></div>
        <div className="col-6"><Field label="Name">{(p) => <input {...p} className="input" value={f.name ?? ''} onChange={set('name')} />}</Field></div>
        <div className="col-6"><Field label="Shown as" hint="Label in inventory apps">{(p) => <input {...p} className="input" value={f.displayName ?? ''} onChange={set('displayName')} />}</Field></div>
        <div className="col-6"><Field label="Contact">{(p) => <input {...p} className="input" value={f.contactName ?? ''} onChange={set('contactName')} />}</Field></div>
        <div className="col-6"><Field label="Mobile">{(p) => <input {...p} className="input input--mono" value={f.mobile ?? ''} onChange={set('mobile')} />}</Field></div>
        <div className="col-12"><Field label="Address">{(p) => <textarea {...p} className="input" rows={2} value={f.address ?? ''} onChange={set('address')} />}</Field></div>
        <div className="col-12"><Switch checked={!!f.stockChecked} onChange={(v) => setF({ ...f, stockChecked: v })} label="Included in stock-taking" showLabel /></div>
        {m.error ? <div className="col-12"><Callout tone="bad">{errText(m.error)}</Callout></div> : null}
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------- Sales tax

const TAX: Record<'ppn' | 'pph23', { label: string; note: string }> = {
  ppn: { label: 'PPN', note: 'Value-added tax on invoices. CRM and sales apps read the active rate.' },
  pph23: { label: 'PPh 23', note: 'Withholding on services. Applied by purchasing and vendor apps.' },
};

function Taxes({ d, canEdit }: { d: Overview; canEdit: boolean }) {
  const [adding, setAdding] = useState<'ppn' | 'pph23' | null>(null);
  const { ask, node } = useConfirm();
  const activate = useSave((id: string) => patch(`/api/master/taxes/${id}`, { active: true }));
  return (
    <Reveal className="grid-12">
      {(['ppn', 'pph23'] as const).map((type, i) => {
        const rates = d.taxRates.filter((t) => t.type === type);
        const current = rates.find((t) => t.active);
        return (
          <Reveal.Item key={type} className={i === 0 ? 'col-7' : 'col-5'}>
            <div className="panel" style={{ padding: 26, display: 'grid', gap: 18 }}>
              <div className="row row--between">
                <div className="eyebrow">{TAX[type].label}</div>
                {canEdit ? <Button size="sm" variant="ghost" icon="plus" onClick={() => setAdding(type)}>New rate</Button> : null}
              </div>
              <div className="row" style={{ alignItems: 'baseline', gap: 14 }}>
                <span className="display num" style={{ fontSize: i === 0 ? '5.5rem' : '4rem', lineHeight: 0.9, color: current ? 'var(--ink-1)' : 'var(--ink-4)' }}>
                  {current ? Number(current.percentage).toLocaleString('id-ID', { maximumFractionDigits: 4 }) : '—'}
                </span>
                <span className="display" style={{ fontSize: '1.8rem', color: 'var(--accent-text)' }}>%</span>
              </div>
              <p className="muted" style={{ fontSize: 'var(--step--1)', maxWidth: '44ch' }}>{current ? `${current.name} — active.` : 'No active rate.'} {TAX[type].note}</p>
              {rates.length > 1 || (rates.length && !current) ? (
                <ul className="stack" style={{ ['--gap' as string]: '6px', listStyle: 'none', padding: 0, margin: 0 }}>
                  {rates.filter((r) => !r.active).map((r) => (
                    <li key={r.id} className="row row--between" style={{ fontSize: 'var(--step--1)' }}>
                      <span className="faint">{r.name} · {Number(r.percentage)}%</span>
                      {canEdit ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={async () => {
                            if (await ask(`Make ${r.name} the active ${TAX[type].label} rate?`, 'Every GX app that reads the active rate (through the ERP bridge) will use it on new documents from now on.', 'Make active')) activate.mutate(r.id);
                          }}
                        >
                          Make active
                        </Button>
                      ) : null}
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          </Reveal.Item>
        );
      })}
      <Reveal.Item className="col-12">
        <Callout tone="info" title="Whole percents downstream">
          The legacy ERP column holds whole numbers, so apps still reading it see a rate rounded to the nearest percent. Hearth keeps four decimals.
        </Callout>
      </Reveal.Item>
      {adding ? <TaxDialog type={adding} onClose={() => setAdding(null)} /> : null}
      {node}
    </Reveal>
  );
}

function TaxDialog({ type, onClose }: { type: 'ppn' | 'pph23'; onClose: () => void }) {
  const [f, setF] = useState({ name: '', percentage: '', active: true });
  const m = useSave(() => post('/api/master/taxes', { type, name: f.name || `${TAX[type].label} ${f.percentage}%`, percentage: f.percentage, active: f.active }), onClose);
  return (
    <Form open onClose={onClose} eyebrow={TAX[type].label} title="Add a rate" onSubmit={() => m.mutate(undefined)} pending={m.isPending} error={m.error}>
      <div className="col-4"><Field label="Rate (%)">{(p) => <input {...p} className="input input--mono" inputMode="decimal" value={f.percentage} onChange={(e) => setF({ ...f, percentage: e.target.value })} />}</Field></div>
      <div className="col-8"><Field label="Name">{(p) => <input {...p} className="input" value={f.name} placeholder={`${TAX[type].label} ${f.percentage || '…'}%`} onChange={(e) => setF({ ...f, name: e.target.value })} />}</Field></div>
      <div className="col-12"><Switch checked={f.active} onChange={(v) => setF({ ...f, active: v })} label="Make it the active rate now (retires the current one)" showLabel /></div>
    </Form>
  );
}

// ---------------------------------------------------------------- Links & settings

function Links({ d, canEdit }: { d: Overview; canEdit: boolean }) {
  const [addLink, setAddLink] = useState(false);
  const [setting, setSetting] = useState<Partial<Overview['settings'][number]> | null>(null);
  const toggle = useSave((l: { id: string; enabled: boolean }) => patch(`/api/master/links/${l.id}`, { enabled: l.enabled }));
  const remove = useSave((id: string) => api(`/api/master/settings/${id}`, { method: 'DELETE' }));
  const co = (id: string) => d.companies.find((c) => c.id === id)?.code ?? '—';
  return (
    <Reveal className="grid-12">
      <Reveal.Item className="col-7">
        <Panel eyebrow={`Software links · ${d.links.length}`} title="Apps each company uses" flush action={canEdit ? <Button size="sm" icon="plus" onClick={() => setAddLink(true)}>Link</Button> : undefined}>
          {d.links.length ? (
            <table className="table">
              <tbody>
                {d.links.map((l) => (
                  <tr key={l.id}>
                    <td style={{ width: 28 }}><Icon name="link" width={16} height={16} /></td>
                    <td>
                      <div>{l.name}</div>
                      <a className="mono faint" style={{ fontSize: 'var(--step--1)' }} href={l.url} target="_blank" rel="noreferrer noopener">{new URL(l.url).host}</a>
                    </td>
                    <td className="mono muted">{co(l.companyId)}</td>
                    <td className="right">{canEdit ? <Switch checked={l.enabled} onChange={(v) => toggle.mutate({ id: l.id, enabled: v })} label={`${l.name} enabled`} /> : l.enabled ? <Chip tone="ok">On</Chip> : <Chip>Off</Chip>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div style={{ padding: 22 }}><p className="muted">No links yet. These power the app launcher in downstream GX apps.</p></div>
          )}
        </Panel>
      </Reveal.Item>
      <Reveal.Item className="col-5">
        <Panel eyebrow={`Company settings · ${d.settings.length}`} title="Keys other apps read" action={canEdit ? <Button size="sm" icon="plus" onClick={() => setSetting({ companyId: d.companies[0]?.id })}>Key</Button> : undefined}>
          {d.settings.length ? (
            <dl className="dl" style={{ gridTemplateColumns: 'minmax(0, 1fr) auto' }}>
              {d.settings.map((x) => (
                <div key={x.id} style={{ display: 'contents' }}>
                  <dt>
                    <span className="mono">{x.key}</span> <span className="faint">· {co(x.companyId)}</span>
                  </dt>
                  <dd className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                    <span className="mono">{x.value ?? (x.json ? 'json' : '—')}</span>
                    {canEdit ? (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => setSetting(x)}>Edit</Button>
                        <Button size="sm" variant="ghost" iconOnly icon="x" aria-label={`Remove ${x.key}`} onClick={() => remove.mutate(x.id)} />
                      </>
                    ) : null}
                  </dd>
                </div>
              ))}
            </dl>
          ) : (
            <p className="muted">None. CRM reads <span className="mono">crm-config-name</span> from here.</p>
          )}
        </Panel>
      </Reveal.Item>
      <LinkDialog open={addLink} onClose={() => setAddLink(false)} d={d} />
      {setting ? <SettingDialog d={d} value={setting} onClose={() => setSetting(null)} /> : null}
    </Reveal>
  );
}

function LinkDialog({ open, onClose, d }: { open: boolean; onClose: () => void; d: Overview }) {
  const [f, setF] = useState({ companyId: '', name: '', identifier: '', url: '' });
  const m = useSave(() => post('/api/master/links', { companyId: f.companyId || d.companies[0]?.id, name: f.name, identifier: f.identifier || null, url: f.url, enabled: true }), () => { setF({ companyId: '', name: '', identifier: '', url: '' }); onClose(); });
  return (
    <Form open={open} onClose={onClose} eyebrow="Software link" title="Add an app link" onSubmit={() => m.mutate(undefined)} pending={m.isPending} error={m.error}>
      <div className="col-6"><Field label="Company">{(p) => <select {...p} className="select" value={f.companyId} onChange={(e) => setF({ ...f, companyId: e.target.value })}>{d.companies.map((c) => <option key={c.id} value={c.id}>{c.code}</option>)}</select>}</Field></div>
      <div className="col-6"><Field label="Identifier" hint="Machine name, e.g. crm">{(p) => <input {...p} className="input input--mono" value={f.identifier} onChange={(e) => setF({ ...f, identifier: e.target.value })} />}</Field></div>
      <div className="col-12"><Field label="Name">{(p) => <input {...p} className="input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />}</Field></div>
      <div className="col-12"><Field label="URL">{(p) => <input {...p} className="input input--mono" value={f.url} placeholder="https://" onChange={(e) => setF({ ...f, url: e.target.value })} />}</Field></div>
    </Form>
  );
}

function SettingDialog({ d, value, onClose }: { d: Overview; value: Partial<Overview['settings'][number]>; onClose: () => void }) {
  const [f, setF] = useState({ companyId: value.companyId ?? d.companies[0]?.id ?? '', key: value.key ?? '', value: value.value ?? '' });
  const m = useSave(() => put('/api/master/settings', { companyId: f.companyId, officeId: value.officeId ?? null, key: f.key, value: f.value || null }), onClose);
  return (
    <Form open onClose={onClose} eyebrow="Company setting" title={value.id ? value.key : 'Add a key'} onSubmit={() => m.mutate(undefined)} pending={m.isPending} error={m.error}>
      <div className="col-6"><Field label="Company">{(p) => <select {...p} className="select" value={f.companyId} disabled={!!value.id} onChange={(e) => setF({ ...f, companyId: e.target.value })}>{d.companies.map((c) => <option key={c.id} value={c.id}>{c.code}</option>)}</select>}</Field></div>
      <div className="col-6"><Field label="Key" hint="No spaces">{(p) => <input {...p} className="input input--mono" value={f.key} disabled={!!value.id} onChange={(e) => setF({ ...f, key: e.target.value })} />}</Field></div>
      <div className="col-12"><Field label="Value">{(p) => <input {...p} className="input input--mono" value={f.value} onChange={(e) => setF({ ...f, value: e.target.value })} />}</Field></div>
    </Form>
  );
}

// ---------------------------------------------------------------- Companies & offices

interface Structure {
  companies: { id: string; code: string; name: string; legalName: string | null; npwp: string | null; email: string | null; phone: string | null; website: string | null; isPrimary: boolean; taxRegime: 'standard' | 'umkm'; payrollBankAccountNo: string | null; info: Record<string, string> | null; deletedAt: string | null }[];
  locations: { id: string; name: string; description: string | null; deletedAt: string | null }[];
  offices: { id: string; companyId: string; locationId: string | null; code: string | null; name: string; address: string | null; email: string | null; phone: string | null; timeZone: string; geofenceLat: number | null; geofenceLng: number | null; geofenceRadiusM: number | null; trustedNetworks: string[] | null; isPublished: boolean; deletedAt: string | null }[];
  companyLocations: { companyId: string; locationId: string }[];
}
const ZONES: [string, string][] = [['Asia/Jakarta', 'WIB · UTC+7'], ['Asia/Makassar', 'WITA · UTC+8'], ['Asia/Jayapura', 'WIT · UTC+9'], ['Asia/Kuala_Lumpur', 'Kuala Lumpur · UTC+8'], ['Asia/Singapore', 'Singapore · UTC+8'], ['UTC', 'UTC']];

function Companies({ d, canEdit }: { d: Overview; canEdit: boolean }) {
  const q = useQuery({ queryKey: ['master-structure'], queryFn: () => get<Structure>('/api/master/structure') });
  const [company, setCompany] = useState<Partial<Structure['companies'][number]> | null>(null);
  const [office, setOffice] = useState<Partial<Structure['offices'][number]> | null>(null);
  if (!q.data) return <Skeleton h={300} />;
  const st = q.data;
  const live = st.companies.filter((c) => !c.deletedAt).sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary) || a.code.localeCompare(b.code));
  return (
    <Reveal className="grid-12">
      {canEdit ? (
        <Reveal.Item className="col-12 row" style={{ gap: 8, justifyContent: 'flex-end' }}>
          <Button size="sm" icon="plus" onClick={() => setOffice({ companyId: live[0]?.id, timeZone: 'Asia/Makassar', geofenceRadiusM: 150, isPublished: true })}>Office</Button>
          <Button size="sm" variant="primary" icon="plus" onClick={() => setCompany({ taxRegime: 'umkm' })}>Company</Button>
        </Reveal.Item>
      ) : null}
      {live.map((c) => {
        const offs = st.offices.filter((o) => o.companyId === c.id && !o.deletedAt);
        return (
          <Reveal.Item key={c.id} className={c.isPrimary ? 'col-12' : 'col-6'}>
            <div className="panel" style={{ padding: 22, display: 'grid', gap: 14, height: '100%' }}>
              <div className="row row--between" style={{ gap: 12 }}>
                <div className="row" style={{ gap: 10 }}>
                  <span className="mono eyebrow">{c.code}</span>
                  {c.isPrimary ? <Chip tone="info">Primary</Chip> : null}
                  <Chip tone={c.taxRegime === 'umkm' ? 'warn' : 'plain'}>{c.taxRegime === 'umkm' ? 'UMKM tax' : 'PPh 21'}</Chip>
                </div>
                {canEdit ? <Button size="sm" variant="ghost" onClick={() => setCompany(c)}>Edit</Button> : null}
              </div>
              <div className="display" style={{ fontSize: c.isPrimary ? '2rem' : '1.4rem', lineHeight: 1.1 }}>{c.name}</div>
              {offs.length ? (
                <ul className="list-plain">
                  {offs.map((o) => (
                    <li key={o.id} className="mini-row row row--between" style={{ gap: 12 }}>
                      <span style={{ minWidth: 0 }}>
                        <span>{o.name}</span> <span className="faint mono" style={{ fontSize: 10.5 }}>{o.code ?? ''}</span>
                        <div className="faint" style={{ fontSize: 'var(--step--1)' }}>
                          {ZONES.find(([z]) => z === o.timeZone)?.[1] ?? o.timeZone} · {o.geofenceLat != null ? `geofence ${o.geofenceRadiusM ?? 150} m` : 'no geofence'}{o.trustedNetworks?.length ? ` · ${o.trustedNetworks.length} office network${o.trustedNetworks.length === 1 ? '' : 's'}` : ''}{o.isPublished ? '' : ' · hidden'}
                        </div>
                      </span>
                      {canEdit ? <Button size="sm" variant="ghost" onClick={() => setOffice(o)}>Edit</Button> : null}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="faint" style={{ fontSize: 'var(--step--1)' }}>No offices.</p>
              )}
            </div>
          </Reveal.Item>
        );
      })}
      <Reveal.Item className="col-12">
        <p className="faint" style={{ fontSize: 'var(--step--1)' }}>{d.nationalities} nationalities on file. Locations: {st.locations.filter((l) => !l.deletedAt).map((l) => l.name).join(', ') || 'none'}.</p>
      </Reveal.Item>
      {company ? <CompanyDialog value={company} st={st} onClose={() => setCompany(null)} /> : null}
      {office ? <OfficeDialog value={office} st={st} onClose={() => setOffice(null)} /> : null}
    </Reveal>
  );
}

function useStructureSave<T>(fn: (v: T) => Promise<unknown>, done: () => void) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['master-structure'] });
      void qc.invalidateQueries({ queryKey: ['master'] });
      void qc.invalidateQueries({ queryKey: ['lookups'] });
      done();
    },
  });
}

function CompanyDialog({ value, st, onClose }: { value: Partial<Structure['companies'][number]>; st: Structure; onClose: () => void }) {
  const [f, setF] = useState({ code: value.code ?? '', name: value.name ?? '', legalName: value.legalName ?? '', npwp: value.npwp ?? '', email: value.email ?? '', phone: value.phone ?? '', website: value.website ?? '', isPrimary: !!value.isPrimary, taxRegime: value.taxRegime ?? 'umkm', payrollBankAccountNo: value.payrollBankAccountNo ?? '', description: value.info?.description ?? '', locationIds: st.companyLocations.filter((x) => x.companyId === value.id).map((x) => x.locationId) });
  const n = (v: string) => (v.trim() ? v.trim() : null);
  const body = { ...f, legalName: n(f.legalName), npwp: n(f.npwp), email: n(f.email), phone: n(f.phone), website: n(f.website), payrollBankAccountNo: n(f.payrollBankAccountNo), description: n(f.description) };
  const m = useStructureSave(() => (value.id ? patch(`/api/master/companies/${value.id}`, body) : post('/api/master/companies', body)), onClose);
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  return (
    <Form open onClose={onClose} eyebrow={value.id ? 'Company' : 'New company'} title={value.id ? (value.name ?? 'Company') : 'Add a company'} onSubmit={() => m.mutate(undefined)} pending={m.isPending} error={m.error}>
      <div className="col-4"><Field label="Code">{(p) => <input {...p} className="input input--mono" value={f.code} onChange={set('code')} />}</Field></div>
      <div className="col-8"><Field label="Name">{(p) => <input {...p} className="input" value={f.name} onChange={set('name')} />}</Field></div>
      <div className="col-6"><Field label="NPWP">{(p) => <input {...p} className="input input--mono" value={f.npwp} onChange={set('npwp')} />}</Field></div>
      <div className="col-6"><Field label="Payroll account (BCA)" hint="Debit account for bank files">{(p) => <input {...p} className="input input--mono" inputMode="numeric" value={f.payrollBankAccountNo} onChange={set('payrollBankAccountNo')} />}</Field></div>
      <div className="col-6"><Field label="E-mail">{(p) => <input {...p} className="input" value={f.email} onChange={set('email')} />}</Field></div>
      <div className="col-6"><Field label="Website">{(p) => <input {...p} className="input input--mono" value={f.website} placeholder="https://" onChange={set('website')} />}</Field></div>
      <div className="col-12"><Field label="Tax regime" hint="UMKM companies are treated as second companies in payroll (split pay, goods-purchase invoices).">{(p) => <select {...p} className="select" value={f.taxRegime} onChange={(e) => setF({ ...f, taxRegime: e.target.value as 'standard' | 'umkm' })}><option value="standard">Standard (PPh 21)</option><option value="umkm">UMKM (PP 55/2022)</option></select>}</Field></div>
      <div className="col-12 stack" style={{ ['--gap' as string]: '8px' }}>
        <div className="eyebrow">Locations</div>
        <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
          {st.locations.filter((l) => !l.deletedAt).map((l) => (
            <button key={l.id} type="button" className="chip-toggle" aria-pressed={f.locationIds.includes(l.id)} onClick={() => setF({ ...f, locationIds: f.locationIds.includes(l.id) ? f.locationIds.filter((x) => x !== l.id) : [...f.locationIds, l.id] })}>{l.name}</button>
          ))}
        </div>
      </div>
      <div className="col-12"><Switch checked={f.isPrimary} onChange={(v) => setF({ ...f, isPrimary: v })} label="Primary company (only one; employs staff under PPh 21)" showLabel /></div>
    </Form>
  );
}

function OfficeDialog({ value, st, onClose }: { value: Partial<Structure['offices'][number]>; st: Structure; onClose: () => void }) {
  const [f, setF] = useState({ companyId: value.companyId ?? '', locationId: value.locationId ?? '', code: value.code ?? '', name: value.name ?? '', address: value.address ?? '', phone: value.phone ?? '', timeZone: value.timeZone ?? 'Asia/Makassar', lat: value.geofenceLat?.toString() ?? '', lng: value.geofenceLng?.toString() ?? '', radius: String(value.geofenceRadiusM ?? 150), networks: (value.trustedNetworks ?? []).join('\n'), isPublished: value.isPublished ?? true });
  const [locating, setLocating] = useState(false);
  const addr = useQuery({ queryKey: ['client-address'], queryFn: () => get<{ ip: string | null; viaProxyOnly: boolean }>('/api/master/client-address'), staleTime: 60_000 });
  const networks = f.networks.split(/[\n,]/).map((x) => x.trim()).filter(Boolean);
  const toast = useToast();
  const num = (v: string) => (v.trim() === '' ? null : Number(v));
  const body = { companyId: f.companyId, locationId: f.locationId || null, code: f.code || null, name: f.name, address: f.address || null, phone: f.phone || null, timeZone: f.timeZone, geofenceLat: num(f.lat), geofenceLng: num(f.lng), geofenceRadiusM: num(f.radius), trustedNetworks: networks, isPublished: f.isPublished };
  const m = useStructureSave(() => (value.id ? patch(`/api/master/offices/${value.id}`, body) : post('/api/master/offices', body)), onClose);
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const here = () => {
    if (!navigator.geolocation) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocating(false);
        setF((x) => ({ ...x, lat: pos.coords.latitude.toFixed(6), lng: pos.coords.longitude.toFixed(6) }));
        toast({ title: `Location set (±${Math.round(pos.coords.accuracy)} m)` });
      },
      () => {
        setLocating(false);
        toast({ title: 'Location unavailable', tone: 'error' });
      },
      { enableHighAccuracy: true, timeout: 10_000 },
    );
  };
  const zoneChanged = value.id && value.timeZone && value.timeZone !== f.timeZone;
  const networkError = m.error instanceof ApiError ? ((m.error.details as Record<string, string[]> | undefined)?.trustedNetworks ?? []).join(' ') || null : null;
  const myIp = addr.data?.ip ?? null;
  const listed = !!myIp && networks.some((n) => n === myIp || n === `${myIp}/${myIp.includes(':') ? 128 : 32}`);
  return (
    <Form open onClose={onClose} eyebrow={value.id ? 'Office' : 'New office'} title={value.id ? (value.name ?? 'Office') : 'Add an office'} onSubmit={() => m.mutate(undefined)} pending={m.isPending} error={m.error}>
      <div className="col-6"><Field label="Company">{(p) => <select {...p} className="select" value={f.companyId} onChange={set('companyId')}>{st.companies.filter((c) => !c.deletedAt).map((c) => <option key={c.id} value={c.id}>{c.code} — {c.name}</option>)}</select>}</Field></div>
      <div className="col-6"><Field label="Location">{(p) => <select {...p} className="select" value={f.locationId} onChange={set('locationId')}><option value="">—</option>{st.locations.filter((l) => !l.deletedAt).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}</select>}</Field></div>
      <div className="col-8"><Field label="Name">{(p) => <input {...p} className="input" value={f.name} onChange={set('name')} />}</Field></div>
      <div className="col-4"><Field label="Code">{(p) => <input {...p} className="input input--mono" value={f.code} onChange={set('code')} />}</Field></div>
      <div className="col-12"><Field label="Time zone" hint="Shifts, scans and payroll cut-offs use this zone.">{(p) => <select {...p} className="select" value={f.timeZone} onChange={set('timeZone')}>{ZONES.map(([z, l]) => <option key={z} value={z}>{l} — {z}</option>)}</select>}</Field></div>
      {zoneChanged ? <div className="col-12"><Callout tone="warn" title="Changing the zone moves every shift boundary">Rebuild recent timesheets for this office afterwards (Attendance → Rebuild). Days locked by a payroll run do not change.</Callout></div> : null}
      <div className="col-4"><Field label="Latitude">{(p) => <input {...p} className="input input--mono" inputMode="decimal" value={f.lat} onChange={set('lat')} />}</Field></div>
      <div className="col-4"><Field label="Longitude">{(p) => <input {...p} className="input input--mono" inputMode="decimal" value={f.lng} onChange={set('lng')} />}</Field></div>
      <div className="col-4"><Field label="Radius (m)">{(p) => <input {...p} className="input input--mono" inputMode="numeric" value={f.radius} onChange={set('radius')} />}</Field></div>
      <div className="col-12 row row--between">
        <Button size="sm" variant="ghost" icon="pin" loading={locating} onClick={here}>Use my current location</Button>
        <Switch checked={f.isPublished} onChange={(v) => setF({ ...f, isPublished: v })} label="Visible to other apps" showLabel />
      </div>
      <div className="col-12">
        <Field label="Office networks for check-in" hint="One per line, IPv4 or IPv6: 203.0.113.16/28, 2001:db8:12::/48. A bare address is one host." error={networkError}>
          {(p) => <textarea {...p} className="input input--mono" rows={3} value={f.networks} onChange={set('networks')} placeholder={'203.0.113.16/28\n2001:db8:12::/48'} spellCheck={false} />}
        </Field>
      </div>
      <div className="col-12 stack" style={{ ['--gap' as string]: '8px' }}>
        <p className="faint" style={{ fontSize: 'var(--step--1)', margin: 0 }}>
          A web check-in from one of these addresses passes without GPS, as an alternative to the geofence. Enter the address Hearth sees for the office connection: its public IP (ask the ISP for a static one), or the internal range if staff reach Hearth over a VPN. It only works while they actually use that connection, so not on mobile data, and not through a VPN that exits somewhere else. If the office line also has IPv6, add its IPv6 prefix too. With networks and no geofence, check-in works only from these networks.
        </p>
        {myIp ? (
          <div className="row row--wrap" style={{ gap: 8, fontSize: 'var(--step--1)' }}>
            <span className="faint">This connection reaches Hearth as <span className="mono">{myIp}</span>.</span>
            {addr.data?.viaProxyOnly ? (
              <span className="faint">That is a proxy of ours, not your connection: set TRUSTED_PROXY_CIDRS on the server first.</span>
            ) : (
              <Button size="sm" variant="ghost" icon="plus" disabled={listed} onClick={() => setF((x) => ({ ...x, networks: [...x.networks.split(/[\n,]/).map((n) => n.trim()).filter(Boolean), myIp].join('\n') }))}>
                {listed ? 'Listed' : 'Add it'}
              </Button>
            )}
          </div>
        ) : null}
      </div>
      <div className="col-12"><Field label="Address">{(p) => <textarea {...p} className="input" rows={2} value={f.address} onChange={set('address')} />}</Field></div>
    </Form>
  );
}
